import React, { useState, useEffect } from 'react';
import { X, Mail, MapPin, Globe, Phone, Send, UserPlus, MessageCircle } from 'lucide-react';
import { useAPI } from '../../../hooks/useAPI';
import { useFriends } from '../../../hooks/useFriends';

const PublicUserProfile = ({ userId, currentUserId, onClose }) => {
  const { apiCall } = useAPI();
  const { sendRequest } = useFriends();
  
  const [userProfile, setUserProfile] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [friendshipStatus, setFriendshipStatus] = useState(null); // 'friends', 'pending', 'sent', null
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadUserProfile();
  }, [userId, currentUserId]);

  const loadUserProfile = async () => {
    try {
      // Get user profile
      const userResponse = await apiCall(`/users/${userId}/profile`);
      setUserProfile(userResponse);
      
      // Get user's posts
      const postsResponse = await apiCall(`/users/${userId}/posts`);
      // Sort posts by created_at in descending order (newest first)
      const sortedPosts = (postsResponse.posts || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setUserPosts(sortedPosts);
      
      // Check friendship status
      await checkFriendshipStatus();
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkFriendshipStatus = async () => {
    try {
      const response = await apiCall(`/friends/status/${userId}`);
      setFriendshipStatus(response.status); // 'friends', 'pending_received', 'pending_sent', null
    } catch (err) {
      console.error('Error checking friendship status:', err);
      setFriendshipStatus(null);
    }
  };

  const handleSendFriendRequest = async () => {
    setActionLoading(true);
    try {
      await sendRequest(userId);
      setFriendshipStatus('pending_sent');
      alert('Friend request sent!');
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendMessage = async () => {
    try {
      // Navigate to messages with this user
      // This should redirect to messages page with the conversation opened
      window.location.hash = `#message-${userId}`;
      onClose();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAcceptFriendRequest = async () => {
    setActionLoading(true);
    try {
      // Get the friendship request ID and accept it
      const response = await apiCall(`/friends/accept/${userId}`, { method: 'POST' });
      setFriendshipStatus('friends');
      alert('Friend request accepted!');
      await checkFriendshipStatus();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectFriendRequest = async () => {
    setActionLoading(true);
    try {
      const response = await apiCall(`/friends/reject/${userId}`, { method: 'POST' });
      setFriendshipStatus(null);
      alert('Friend request rejected');
      await checkFriendshipStatus();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 text-center">
          <p className="text-gray-600">Profile not found</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-3xl my-8">
        {/* Header Section */}
        <div className="border-b p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-start gap-4 flex-1">
              {/* Profile Picture */}
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0">
                {userProfile.profile_picture ? (
                  <img 
                    src={userProfile.profile_picture} 
                    alt={userProfile.username} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-bold text-3xl">
                    {userProfile.username[0].toUpperCase()}
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-gray-800">{userProfile.username}</h2>
                <p className="text-sm text-gray-600">
                  Member since {new Date(userProfile.created_at).toLocaleDateString()}
                </p>
                
                {userProfile.bio && (
                  <p className="text-gray-700 mt-3 line-clamp-2">{userProfile.bio}</p>
                )}

                {/* Contact Information */}
                <div className="flex flex-wrap gap-4 mt-4">
                  {userProfile.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail size={16} />
                      <span>{userProfile.email}</span>
                    </div>
                  )}
                  {userProfile.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone size={16} />
                      <span>{userProfile.phone}</span>
                    </div>
                  )}
                  {userProfile.location && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin size={16} />
                      <span>{userProfile.location}</span>
                    </div>
                  )}
                  {userProfile.website && (
                    <a 
                      href={userProfile.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                    >
                      <Globe size={16} />
                      <span>Website</span>
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Close Button */}
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 p-2"
            >
              <X size={28} />
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {friendshipStatus === 'friends' ? (
              // Friends - Show Send Message Button
              <button
                onClick={handleSendMessage}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold"
              >
                <MessageCircle size={18} />
                Send Message
              </button>
            ) : friendshipStatus === 'pending_received' ? (
              // Received Friend Request - Show Accept/Reject
              <div className="flex gap-2 w-full">
                <button
                  onClick={handleAcceptFriendRequest}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-semibold disabled:opacity-50"
                >
                  ✓ Accept Request
                </button>
                <button
                  onClick={handleRejectFriendRequest}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-semibold disabled:opacity-50"
                >
                  ✕ Reject
                </button>
              </div>
            ) : friendshipStatus === 'pending_sent' ? (
              // Sent Friend Request - Show Cancel
              <button
                disabled
                className="flex-1 px-4 py-2 bg-gray-400 text-white rounded-lg font-semibold cursor-not-allowed"
              >
                ⏳ Request Pending
              </button>
            ) : (
              // Not Friends - Show Send Friend Request
              <button
                onClick={handleSendFriendRequest}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition font-semibold disabled:opacity-50"
              >
                <UserPlus size={18} />
                {actionLoading ? 'Sending...' : 'Add as Friend'}
              </button>
            )}
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-200">
              <p className="text-2xl font-bold text-purple-600">{userPosts.length}</p>
              <p className="text-sm text-gray-600">Posts</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
              <p className="text-2xl font-bold text-blue-600">{userProfile.friends_count || 0}</p>
              <p className="text-sm text-gray-600">Friends</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
              <p className="text-2xl font-bold text-green-600">{userProfile.reputation || 0}</p>
              <p className="text-sm text-gray-600">Reputation</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-200">
              <p className="text-2xl font-bold text-yellow-600">{userProfile.verified ? '✓' : '-'}</p>
              <p className="text-sm text-gray-600">Verified</p>
            </div>
          </div>
        </div>

        {/* Posts Section */}
        <div className="p-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">
            Posts by {userProfile.username}
          </h3>

          {userPosts.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-600 text-lg">No posts yet</p>
              <p className="text-sm text-gray-500 mt-2">This user hasn't made any posts</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {userPosts.map((post) => (
                <div 
                  key={post.id} 
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition"
                >
                  {/* Post Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-purple-600">
                        {post.category.replace('_', ' ').toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(post.created_at).toLocaleDateString()} at {new Date(post.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    {post.status === 'resolved' && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                        ✓ Reunited
                      </span>
                    )}
                  </div>

                  {/* Pet Info */}
                  {post.pet_name && (
                    <p className="text-sm font-semibold text-blue-900 mb-2">
                      🐾 {post.pet_name} {post.pet_type ? `(${post.pet_type})` : ''}
                    </p>
                  )}

                  {/* Post Content */}
                  <p className="text-gray-700 text-sm mb-2 line-clamp-3">{post.content}</p>

                  {/* Media Thumbnail */}
                  {post.media_urls && post.media_urls.length > 0 && (
                    <div className="flex gap-2 mb-2">
                      {post.media_urls.slice(0, 2).map((url, idx) => (
                        <img
                          key={idx}
                          src={url}
                          alt={`Post ${idx}`}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ))}
                      {post.media_urls.length > 2 && (
                        <div className="w-16 h-16 bg-gray-300 rounded flex items-center justify-center text-sm font-bold text-gray-600">
                          +{post.media_urls.length - 2}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Location */}
                  {post.location_name && (
                    <p className="text-xs text-gray-600 flex items-center gap-1">
                      <MapPin size={12} />
                      {post.location_name}
                    </p>
                  )}

                  {/* Contact Info */}
                  {post.contact_info && (
                    <p className="text-xs text-blue-600 mt-2">
                      📞 {post.contact_info}
                    </p>
                  )}

                  {/* Engagement Stats */}
                  <div className="flex gap-4 mt-3 pt-2 border-t text-xs text-gray-600">
                    <span>❤️ {post.likes_count}</span>
                    <span>💬 {post.comments_count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicUserProfile;